import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, Loading, btnPrimary, btnGhost, inputCls, labelCls } from "@/components/kit";
import MacroRing from "@/components/MacroRing";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Heartbeat, Moon, Drop, Fire, Camera, Plus, Minus, Trash, Star, Bed, Sun } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" });
const fmtClock = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// "YYYY-MM-DDTHH:mm" in local time for datetime-local inputs
const toLocalInput = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 16);
};

function TrendCharts({ history }) {
  const series = [
    { key: "sleep_hours", label: "Sleep (h)", color: "#BF5AF2" },
    { key: "water_glasses", label: "Water (cups)", color: "#0A84FF" },
    { key: "calories", label: "Calories", color: "#FF3B30" },
  ];
  const data = history.days.map((d) => ({ ...d, label: fmtDay(d.date) }));
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {series.map((m) => (
        <div key={m.key}>
          <div className="text-[11px] uppercase tracking-widest text-[#737373] mb-2">{m.label}</div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" stroke="#737373" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey={m.key} fill={m.color} radius={[2, 2, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ))}
    </div>
  );
}

function SleepSessionList({ sessions, onDelete }) {
  if (!sessions.length) return <div className="text-sm text-[#737373] py-4">No sleep sessions logged.</div>;
  return (
    <div className="space-y-2" data-testid="sleep-session-list">
      {sessions.map((s) => (
        <div key={s.id} className="flex items-center gap-3 bg-[#0A0A0A] border border-white/10 rounded-sm p-3" data-testid={`session-${s.id}`}>
          <Bed size={18} weight="fill" color="#BF5AF2" />
          <div className="flex-1 min-w-0 text-xs">
            <div className="text-white"><span className="text-[#737373]">Bed</span> {fmtClock(s.sleep_at)}</div>
            <div className="text-white"><span className="text-[#737373]">Wake</span> {fmtClock(s.wake_at)}</div>
          </div>
          <div className="font-mono font-bold text-[#BF5AF2]">{s.hours}h</div>
          {onDelete && <button onClick={() => onDelete(s.id)} className="text-[#737373] hover:text-[#FF3B30]" data-testid={`delete-session-${s.id}`}><Trash size={16} weight="bold" /></button>}
        </div>
      ))}
    </div>
  );
}

function AthleteWellness() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [sum, setSum] = useState(null);
  const [hist, setHist] = useState(null);
  const [nut, setNut] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [water, setWater] = useState(0);
  const defBed = new Date(); defBed.setDate(defBed.getDate() - 1); defBed.setHours(23, 0, 0, 0);
  const defWake = new Date(); defWake.setHours(7, 0, 0, 0);
  const [bed, setBed] = useState(toLocalInput(defBed));
  const [wake, setWake] = useState(toLocalInput(defWake));
  const [quality, setQuality] = useState(3);

  const load = async () => {
    const [w, h, n, l, ss] = await Promise.all([
      api.get("/wellness/summary"), api.get("/wellness/history", { params: { days: 7 } }),
      api.get("/nutrition/summary"), api.get("/nutrition/log", { params: { date: today } }),
      api.get("/wellness/sleep-sessions"),
    ]);
    setSum(w.data); setHist(h.data); setNut(n.data); setLogs(l.data); setSessions(ss.data);
    setWater(w.data.water_glasses || 0);
  };
  useEffect(() => { load(); }, []);

  const addSession = async () => {
    try {
      await api.post("/wellness/sleep-session", { sleep_at: new Date(bed).toISOString(), wake_at: new Date(wake).toISOString(), quality });
      toast.success("Sleep session logged");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log session"); }
  };
  const delSession = async (id) => { await api.delete(`/wellness/sleep-session/${id}`); toast.success("Removed"); load(); };
  const saveWater = async (g) => { setWater(g); await api.post("/wellness/water", { glasses: g }); };
  const delMeal = async (id) => { await api.delete(`/nutrition/log/${id}`); toast.success("Removed"); load(); };

  if (!sum || !hist || !nut) return <Loading />;
  const t = nut.totals, g = nut.goals;

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-7xl mx-auto">
      <PageHeader eyebrow="Recovery & Fuel" title="Wellness" icon={Heartbeat}>
        <button onClick={() => navigate("/app/scan")} className={btnPrimary} data-testid="wellness-scan-button"><Camera size={18} weight="bold" /> Scan a meal</button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 items-start">
        {/* Sleep session logger */}
        <Card className="p-6 lg:col-span-1" data-testid="sleep-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-5"><Moon size={16} weight="fill" color="#BF5AF2" /> Sleep</div>
          <div className="flex items-end gap-2 mb-5"><span className="font-mono text-5xl font-black text-[#BF5AF2]" data-testid="sleep-total">{sum.sleep_hours || 0}</span><span className="text-[#737373] mb-2">h today</span></div>
          <div className="space-y-3 mb-4">
            <div>
              <label className={labelCls}><Bed size={12} className="inline mr-1" /> Went to sleep</label>
              <input type="datetime-local" max={toLocalInput(new Date())} className={inputCls} value={bed} onChange={(e) => setBed(e.target.value)} data-testid="sleep-bed-input" />
            </div>
            <div>
              <label className={labelCls}><Sun size={12} className="inline mr-1" /> Woke up</label>
              <input type="datetime-local" max={toLocalInput(new Date())} className={inputCls} value={wake} onChange={(e) => setWake(e.target.value)} data-testid="sleep-wake-input" />
            </div>
          </div>
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((q) => (
              <button key={q} onClick={() => setQuality(q)} data-testid={`quality-${q}`}><Star size={20} weight={q <= quality ? "fill" : "regular"} color={q <= quality ? "#FF9F0A" : "#444"} /></button>
            ))}
            <span className="text-xs text-[#737373] ml-2">quality</span>
          </div>
          <button onClick={addSession} className={`${btnPrimary} w-full mb-4`} data-testid="save-session-button"><Plus size={16} weight="bold" /> Log Sleep Session</button>
          <SleepSessionList sessions={sessions.slice(0, 5)} onDelete={delSession} />
        </Card>

        {/* Water */}
        <Card className="p-6" data-testid="water-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-5"><Drop size={16} weight="fill" color="#0A84FF" /> Water</div>
          <div className="flex items-end gap-2 mb-5"><span className="font-mono text-5xl font-black text-[#0A84FF]" data-testid="water-count">{water}</span><span className="text-[#737373] mb-2">/ 8 cups</span></div>
          <div className="flex flex-wrap gap-1.5 mb-5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="w-6 h-9 rounded-sm border" style={{ background: i < water ? "#0A84FF" : "transparent", borderColor: i < water ? "#0A84FF" : "rgba(255,255,255,0.15)" }} />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => saveWater(Math.max(0, water - 1))} className={`${btnGhost} flex-1 px-0`} data-testid="water-minus"><Minus size={18} weight="bold" /></button>
            <button onClick={() => saveWater(water + 1)} className={`${btnPrimary} flex-1 px-0`} data-testid="water-plus"><Plus size={18} weight="bold" /></button>
          </div>
        </Card>

        {/* Nutrition */}
        <Card className="p-6" data-testid="nutrition-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-5"><Fire size={16} weight="fill" color="#FF3B30" /> Today's Fuel</div>
          <div className="flex items-end gap-2 mb-3"><span className="font-mono text-5xl font-black">{Math.round(t.calories)}</span><span className="text-[#737373] mb-2">/ {g.calories} kcal</span></div>
          <div className="h-2.5 bg-white/5 rounded-sm overflow-hidden mb-5"><div className="h-full bg-[#FF3B30]" style={{ width: `${Math.min(100, (t.calories / g.calories) * 100)}%` }} /></div>
          <div className="grid grid-cols-3 gap-2">
            <MacroRing value={t.protein} goal={g.protein} color="#FF3B30" label="Protein" size={86} stroke={8} />
            <MacroRing value={t.carbs} goal={g.carbs} color="#FF9F0A" label="Carbs" size={86} stroke={8} />
            <MacroRing value={t.fat} goal={g.fat} color="#34C759" label="Fat" size={86} stroke={8} />
          </div>
        </Card>
      </div>

      <Card className="p-6 mt-6" data-testid="wellness-history">
        <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-5">7-Day Trends</div>
        <TrendCharts history={hist} />
      </Card>

      <div className="mt-8">
        <h2 className="font-head text-2xl font-bold uppercase tracking-tight mb-4">Today's Meals</h2>
        {logs.length === 0 ? (
          <Card className="p-10 text-center border-dashed" data-testid="empty-meals">
            <Fire size={36} weight="duotone" color="#737373" className="mx-auto mb-3" />
            <p className="text-[#A3A3A3] mb-4">No meals logged today.</p>
            <button onClick={() => navigate("/app/scan")} className={btnPrimary}><Camera size={18} weight="bold" /> Scan your first meal</button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="meal-list">
            {logs.map((l) => (
              <Card key={l.id} className="p-4 flex items-center gap-4" data-testid={`meal-${l.id}`}>
                <div className="flex-1 min-w-0"><div className="font-semibold text-white truncate">{l.name}</div><div className="text-xs uppercase tracking-wider text-[#737373]">{l.meal_type}</div></div>
                <div className="hidden sm:flex gap-4 font-mono text-sm"><span className="text-[#FF3B30]">{Math.round(l.protein)}P</span><span className="text-[#FF9F0A]">{Math.round(l.carbs)}C</span><span className="text-[#34C759]">{Math.round(l.fat)}F</span></div>
                <div className="font-mono font-bold w-20 text-right">{Math.round(l.calories)}<span className="text-[#737373] text-xs ml-1">kcal</span></div>
                <button onClick={() => delMeal(l.id)} className="text-[#737373] hover:text-[#FF3B30]" data-testid={`delete-meal-${l.id}`}><Trash size={18} weight="bold" /></button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StaffWellness() {
  const [students, setStudents] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sum, setSum] = useState(null);
  const [hist, setHist] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/users", { params: { role: "athlete" } }).then((r) => {
      setStudents(r.data);
      if (r.data[0]) setSelected(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setSum(null); setHist(null);
    Promise.all([
      api.get("/wellness/summary", { params: { student_id: selected } }),
      api.get("/wellness/history", { params: { student_id: selected, days: 7 } }),
      api.get("/wellness/sleep-sessions", { params: { student_id: selected } }),
    ]).then(([s, h, ss]) => { setSum(s.data); setHist(h.data); setSessions(ss.data); });
  }, [selected]);

  if (!students) return <Loading />;
  const sel = students.find((s) => s.id === selected);

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-7xl mx-auto">
      <PageHeader eyebrow="Athlete Monitoring" title="Wellness" icon={Heartbeat} />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 items-start">
        <Card className="p-3 lg:col-span-1">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold px-2 py-2">Athletes</div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {students.map((s) => (
              <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`wellness-athlete-${s.id}`}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-left transition-colors ${selected === s.id ? "bg-[#FF3B30]/15" : "hover:bg-white/5"}`}>
                <Avatar name={s.name} size={32} color="#34C759" />
                <div className="min-w-0"><div className="text-sm text-white truncate">{s.name}</div><div className="text-[11px] text-[#737373]">{s.sport}</div></div>
              </button>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-3">
          {!sum || !hist ? <Loading /> : (
            <div className="space-y-6">
              {sel && <div className="flex items-center gap-3"><Avatar name={sel.name} size={44} color="#34C759" /><div><div className="font-head text-2xl font-bold uppercase tracking-tight">{sel.name}</div><div className="text-xs text-[#737373]">{sel.sport}</div></div></div>}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Sleep Today</div><div className="font-mono text-3xl font-black text-[#BF5AF2]" data-testid="staff-sleep">{sum.sleep_hours || 0}h</div></Card>
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Water</div><div className="font-mono text-3xl font-black text-[#0A84FF]">{sum.water_glasses || 0}</div></Card>
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Calories</div><div className="font-mono text-3xl font-black text-[#FF3B30]">{Math.round(sum.calories || 0)}</div></Card>
                <Card className="p-5"><div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Meals</div><div className="font-mono text-3xl font-black text-[#34C759]">{sum.meals || 0}</div></Card>
              </div>
              <Card className="p-6" data-testid="staff-wellness-trends">
                <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-5">7-Day Trends</div>
                <TrendCharts history={hist} />
              </Card>
              <Card className="p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-4"><Moon size={16} weight="fill" color="#BF5AF2" /> Recent Sleep Sessions</div>
                <SleepSessionList sessions={sessions.slice(0, 10)} />
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Wellness() {
  const { user } = useAuth();
  return user?.role === "athlete" ? <AthleteWellness /> : <StaffWellness />;
}
